import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button, Field, Input, KeyValue, Section } from '@zt/ui';
import { api, ApiError } from '../lib/api';
import { HelpTip } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { OrderDetail } from '../lib/types';

/**
 * Sipariş ayrıntısında satın alma emri (PO) ve firma içi onay (F7-07, ADR-110): PO numarası (her aşamada
 * düzeltilebilir), PO belgesi (indir, ekle/değiştir) ve Satın alma eşiğini aşan siparişin Yetkili onayı.
 */
export function PurchaseOrderSection({ order }: { order: OrderDetail }) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const queryClient = useQueryClient();
  const writable = can('orders.order.write');
  const [po, setPo] = useState(order.poNumber ?? '');
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const save = useApiMutation(
    () =>
      api.patch(`/v1/admin/orders/${order.id}`, { poNumber: po.trim() || null }, { ifMatch: order.version }),
    { invalidate: [['order', order.id], ['orders']], success: t('common.saved') },
  );
  const a = order.companyApproval;
  const doc = order.poDocument;
  return (
    <Section
      title={t('admin.orders.po.title')}
      description={t('admin.orders.po.lead')}
      action={<HelpTip k="orders.po" />}
    >
      {writable ? (
        <div className="flex flex-wrap items-end gap-2">
          <Field
            label={t('admin.orders.po.number')}
            optionalLabel={t('common.optional')}
            help={<HelpTip k="orders.po_number" />}
            error={fieldError(save.error, 'poNumber')}
          >
            {(p) => (
              <Input
                {...p}
                className="w-56"
                maxLength={50}
                value={po}
                onChange={(e) => setPo(e.target.value)}
              />
            )}
          </Field>
          <Button
            loading={save.isPending}
            disabled={(po.trim() || null) === (order.poNumber ?? null)}
            onClick={() => save.mutate(undefined)}
          >
            {t('admin.orders.po.save')}
          </Button>
        </div>
      ) : (
        <p className="text-sm">
          {t('admin.orders.po.number')}: {order.poNumber ?? '—'}
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium">{t('admin.orders.po.document')}</span>
        {doc ? (
          <a className="text-pine hover:underline" href={`/v1/admin/orders/${order.id}/po-document`}>
            {t('admin.orders.po.download', {
              name: doc.name,
              kb: Math.max(1, Math.round(doc.sizeBytes / 1024)),
            })}
          </a>
        ) : (
          <span className="text-ink-3">{t('admin.orders.po.noDocument')}</span>
        )}
        {writable && (
          <span className="flex items-center gap-1">
            <input
              ref={input}
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              aria-label={doc ? t('admin.orders.po.replace') : t('admin.orders.po.attach')}
              tabIndex={-1}
              className="sr-only"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setBusy(true);
                try {
                  const fd = new FormData();
                  fd.append('file', file);
                  await api.upload(`/v1/admin/orders/${order.id}/po-document`, fd);
                  await queryClient.invalidateQueries({ queryKey: ['order', order.id] });
                  toast.success(t('admin.orders.po.attached'));
                } catch (err) {
                  toast.error(err instanceof ApiError ? err.title : String(err));
                } finally {
                  setBusy(false);
                  if (input.current) input.current.value = '';
                }
              }}
            />
            <Button size="sm" loading={busy} onClick={() => input.current?.click()}>
              {doc ? t('admin.orders.po.replace') : t('admin.orders.po.attach')}
            </Button>
            <HelpTip k="orders.po_document" />
          </span>
        )}
      </div>
      {a && (
        <div className="mt-4">
          <p className="mb-1 flex items-center gap-1 text-sm font-medium">
            {t('admin.orders.po.approval')} <HelpTip k="orders.company_approval" />
          </p>
          <KeyValue
            items={[
              {
                label: t('admin.orders.po.requestedBy'),
                value: `${a.requesterName ?? '—'} · ${formatDate(a.requestedAt, true)}`,
              },
              {
                label: t('admin.orders.po.approvedBy'),
                value: `${a.decidedByName ?? '—'}${a.decidedAt ? ` · ${formatDate(a.decidedAt, true)}` : ''}`,
              },
              { label: t('admin.orders.po.approvedAmount'), value: money(a.goodsTotalMinor, a.currency) },
              ...(a.decisionNote
                ? [{ label: t('admin.orders.po.approvalNote'), value: a.decisionNote }]
                : []),
            ]}
          />
        </div>
      )}
    </Section>
  );
}
